import { FormControl, FormHelperText, FormLabel, Input } from '@mui/joy';

import { Button } from '@/design-system';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { useCurrentOrganization } from '@/hooks/useSwr';
import { ChangePaymentPlanTierSchema } from '@/types/schema';
import { xCount } from '@/utils/stringUtils';

import {
  MemberGroupType,
  MemberStatus,
} from '../types/organizationMembers.type';
import { PAYMENT_PLAN_PRICING_MAP, PROGRAM_NAME } from '../utils/constants';
import { useMembers } from '../utils/useMembers';
import { InviteOrgMemberModal } from './InviteOrgMemberModal';
import MembersTable from './MembersTable';

export const ManageSpendModalCard = (props: {
  onNext: () => void;
  targetPlanTier: ChangePaymentPlanTierSchema | null;
  isLoading: boolean;
  displayName: string;
  setDisplayName: (displayName: string) => void;
}) => {
  const { members: activeMembers } = useMembers(
    MemberStatus.ACTIVE,
    MemberGroupType.ORGANIZATION,
    () => {
      /* no-op */
    }
  );
  const { members: pendingMembers } = useMembers(
    MemberStatus.PENDING,
    MemberGroupType.ORGANIZATION,
    () => {
      /* no-op */
    }
  );
  const auth = useAuth();

  const { data: organization } = useCurrentOrganization();
  const changePlanTier = props.targetPlanTier ?? 'plus';
  const PRICE_PER_SEAT = PAYMENT_PLAN_PRICING_MAP[changePlanTier].pricePerSeat;
  const nMembers =
    (activeMembers.data?.length ?? 0) + (pendingMembers.data?.length ?? 0);

  const totalCost = nMembers * PRICE_PER_SEAT;

  const isUpgradeFromPersonal =
    changePlanTier !== 'developer' && organization?.is_personal;

  const isDisplayNamePersonal = props.displayName?.toLowerCase() === 'personal';

  return (
    <div className="flex flex-col gap-5">
      {isUpgradeFromPersonal && (
        <div className="flex flex-col gap-1">
          <FormControl error={isDisplayNamePersonal}>
            <FormLabel>Organization name</FormLabel>
            <Input
              value={props.displayName}
              onChange={(e) => {
                props.setDisplayName(e.target.value);
              }}
            />
            {isDisplayNamePersonal && (
              <FormHelperText>
                Please update your organization name. We recommend using your
                company or team name.
              </FormHelperText>
            )}
          </FormControl>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <div className="text-lg font-semibold leading-normal">Team Members</div>
        <div className="text-normal text-sm text-tertiary">
          Each seat costs ${PRICE_PER_SEAT} per month on the{' '}
          {PROGRAM_NAME[changePlanTier]}
        </div>
      </div>
      <div className="rounded-lg border border-secondary">
        <div className="m-0 flex items-center justify-between border-b border-secondary px-5 py-4">
          {xCount('member', nMembers)}
          <InviteOrgMemberModal
            changePlanTier={props.targetPlanTier ?? 'plus'}
          />
        </div>
        <MembersTable
          canManage={false}
          isRbacEnabled={false}
          currentUserId={auth.session?.user.id}
          members={activeMembers.data ?? []}
          pendingMembers={pendingMembers.data ?? []}
          groupType="organization"
        />
      </div>
      <div className="inline-flex flex-col items-start justify-start gap-2">
        <div className="flex flex-col items-start justify-start gap-1.5 self-stretch">
          <FormLabel sx={{ fontSize: '14px' }}>Team Cost Preview</FormLabel>
          <div className="inline-flex items-start justify-start self-stretch rounded-lg border border-secondary bg-secondary">
            <div className="flex h-11 shrink grow basis-0 items-center justify-start gap-2 py-2.5 pl-3.5">
              <div className="flex h-6 shrink grow basis-0 items-center justify-start gap-2">
                <div className="text-base font-normal leading-normal text-tertiary">
                  $
                </div>
                <div className="shrink grow basis-0">{totalCost}</div>
              </div>
            </div>
            <div className="flex items-center justify-between self-stretch px-3.5 py-2.5">
              <div className="text-base font-normal leading-normal text-tertiary">
                / billed monthly
              </div>
            </div>
          </div>
          <div className="self-stretch text-sm font-normal leading-tight text-tertiary">
            ${PRICE_PER_SEAT} per seat per month. Seats are charged at the
            beginning of each month. The first month is prorated, and will be
            invoiced within a few hours.
          </div>
        </div>
      </div>
      <div className="h-[1px] self-stretch bg-secondary" />

      <div className="self-end">
        <Button
          type="button"
          size="md"
          color="primary"
          loading={props.isLoading}
          onClick={() => {
            props.onNext();
          }}
          disabled={isDisplayNamePersonal}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
